// THE ENTRY FORM'S FRAME (UI rebuild batch 3) — Astra's form anatomy for Move money, Adjust balance and New party: the
// title, ONE card holding the fields and its footer (a muted note at the left, the one gold primary at the right; on the
// phone the button full width with the note under it), and from lg a context column beside it — the figure the entry
// acts on, two facts, and "About this job" folded. Layout only: no state, no money logic.
import type { ReactNode } from 'react';
import { PageHead, Fact } from '../kit';
import { AboutFold } from '../record/parts';

export function FormFrame({ title, aside, children, below, head = true }: {
  title: string;
  /** false: drawn inside a screen that has its own head (custody's Settings) — no page title of its own. */
  head?: boolean;
  /** The context column (lg only): the current balance or effect. */
  aside?: ReactNode;
  children: ReactNode;
  /** One quiet line under the card (a link to the history). */
  below?: ReactNode;
}) {
  return (
    <div className="lg:max-w-[1160px]">
      {head && <PageHead title={title} />}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,600px)_minmax(280px,1fr)] xl:items-start">
        <div className="min-w-0">
          <section className="space-y-4 rounded-[10px] border border-stroke bg-panel p-4 lg:p-6">{children}</section>
          {below && <div className="mt-4">{below}</div>}
        </div>
        {aside && <aside className="min-w-0 max-xl:hidden">{aside}</aside>}
      </div>
    </div>
  );
}

/** The card's footer: a hairline, the note when there is one (why the button is off; the readback once reviewing), the
 *  actions with the primary last. */
export function FormFoot({ note, children }: { note?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-4 lg:flex-row lg:items-center lg:justify-between lg:pt-5">
      {note ? <div className="min-w-0 text-center text-meta text-tg-hint lg:text-left">{note}</div> : <span className="max-lg:hidden" />}
      <div className="flex flex-col-reverse gap-2 lg:shrink-0 lg:flex-row lg:items-center">{children}</div>
    </div>
  );
}

/** The context column: a heading, the figure, one muted line, facts, and the job's explanation folded. */
export function FormAside({ title, figure, sub, facts, help }: {
  title: string;
  figure: ReactNode;
  sub?: ReactNode;
  facts?: ReadonlyArray<{ label: string; value: ReactNode }>;
  /** The job explained — folded under "About this job". */
  help?: ReactNode;
}) {
  return (
    <div className="pt-1">
      <h2 className="text-body font-semibold">{title}</h2>
      <div className="mt-3 break-words font-mono tabular text-figure text-tg-hint">{figure}</div>
      {sub && <p className="mt-2 text-meta text-tg-hint">{sub}</p>}
      {facts && facts.length > 0 && (
        <div className="mt-5 border-t border-stroke-soft">
          {facts.map((f) => <Fact key={f.label} label={f.label}>{f.value}</Fact>)}
        </div>
      )}
      {help && (
        <details className="mt-5 border-t border-stroke-soft pt-4 text-meta text-tg-hint">
          <summary className="cursor-pointer">About this job</summary>
          <div className="mt-2 space-y-2">{help}</div>
        </details>
      )}
    </div>
  );
}

/** The primary's two faces: "Review …" opens the readback, the confirm posts. Both carry the same disabled-until rule. */
export const PRIMARY = 'btn-primary w-full lg:w-auto lg:px-6';
export const QUIET = 'btn-secondary w-full px-4 text-body lg:w-auto';

/** "About this job" where the aside does not show (the phone): the one fold every rebuilt form shares. */
export function AboutJob({ help, facts, className = '' }: {
  help: ReactNode; facts?: ReadonlyArray<{ label: string; value: ReactNode }>; className?: string;
}) {
  return <AboutFold help={help} facts={facts} className={className} />;
}
